import {
  FIELD_CONFIG,
  SLOT_ORDER,
  SLOT_LABELS,
  SCAFFOLD_HEADER_LABEL,
  SCAFFOLD_CENTRAL_LABEL,
  SCAFFOLD_FIELD_LABEL,
  SCAFFOLD_DEFAULT_VALUE,
  LEGACY_HEADER_LABEL,
  BALANCE_FIELD,
} from './fieldVisibilityConfig';
import { TemplateState } from '../../../../store/types/template';

/** Display name for form/query param: 'Header field', 'Field 1', 'Back field 1', 'Central field', etc. */
export const getTemplateFieldDisplayName = (
  section: string,
  slot: string
): string => {
  if (!section || !slot) return '';
  const slotNum = SLOT_LABELS[slot] ?? slot;
  switch (section) {
    case 'header':
      return 'Header field';
    case 'central':
      return slot === 'one' ? 'Central field' : `Central field ${slotNum}`;
    case 'details':
      return `Field ${slotNum}`;
    case 'back':
      return `Back field ${slotNum}`;
    default:
      return `${section.charAt(0).toUpperCase() + section.slice(1)} ${slot.charAt(0).toUpperCase() + slot.slice(1)}`;
  }
};

/** Derive display name from template field id (e.g. header_one → 'Header field', details_two → 'Field 2'). */
export const getTemplateFieldDisplayNameFromId = (fieldId: string): string => {
  if (!fieldId || typeof fieldId !== 'string') return '';
  const parts = fieldId.split('_');
  if (parts.length >= 2) {
    const section = parts[0];
    const slot = parts[1];
    return getTemplateFieldDisplayName(section, slot);
  }
  return '';
};

const buildScaffoldField = (
  section: string,
  slot: string,
  initialField: { id?: string; label: string; defaultValue?: string | number }
) => ({
  id: initialField.id ?? `${section}_${slot}`,
  label: initialField.label,
  defaultValue: (initialField.defaultValue ?? '') as string,
  _newlyAdded: true,
});

export const buildInitialFields = (templateType: keyof typeof FIELD_CONFIG) => {
  const config = FIELD_CONFIG[templateType] ?? FIELD_CONFIG.loyalty;
  const fields: any = {};

  Object.entries(config).forEach(([section, { initial }]) => {
    fields[section] = {};
    Object.entries(initial).forEach(([slot, initialField]) => {
      fields[section][slot] = buildScaffoldField(section, slot, initialField);
    });
  });

  return fields;
};

const SCAFFOLD_LABELS = new Set([
  '',
  SCAFFOLD_HEADER_LABEL,
  SCAFFOLD_CENTRAL_LABEL,
  SCAFFOLD_FIELD_LABEL,
  LEGACY_HEADER_LABEL,
  BALANCE_FIELD.label,
  'Stamp Label',
]);
const SCAFFOLD_VALUES = new Set(['', SCAFFOLD_DEFAULT_VALUE, '0']);

const isUntouchedScaffoldField = (field: {
  label?: string;
  defaultValue?: string | number;
}) =>
  SCAFFOLD_LABELS.has(field.label ?? '') &&
  SCAFFOLD_VALUES.has(String(field.defaultValue ?? ''));

export const adjustFieldsForTemplateType = (
  current: TemplateState['fields'],
  newType: keyof typeof FIELD_CONFIG
) => {
  const config = FIELD_CONFIG[newType] ?? FIELD_CONFIG.loyalty;
  const next: any = {};

  Object.entries(config).forEach(([section, { initial, limit }]) => {
    next[section] = {};

    SLOT_ORDER.forEach((slot) => {
      const field = current[section]?.[slot];
      if (!field?.id?.trim()) return;
      if (isUntouchedScaffoldField(field) && !initial[slot]) return;
      if (Object.keys(next[section]).length < limit) {
        next[section][slot] = field;
      }
    });

    Object.entries(initial).forEach(([slot, initialField]) => {
      if (!next[section][slot]) {
        next[section][slot] = buildScaffoldField(section, slot, initialField);
      }
    });
  });

  if (newType === 'stampCard' && next.header?.one) {
    next.header.one = {
      ...next.header.one,
      id: 'stamp_field',
      label: 'Stamp Label',
      defaultValue: 0 as unknown as string,
    };
  }

  if (newType !== 'stampCard' && next.header?.one) {
    const headerOneId = next.header.one.id;
    if (headerOneId === 'stamp_field' || headerOneId === 'stampLabel') {
      next.header.one = {
        ...next.header.one,
        id: 'header_one',
        label: SCAFFOLD_HEADER_LABEL,
        defaultValue: SCAFFOLD_DEFAULT_VALUE,
      };
    }
  }

  return next;
};

export const LOYALTY_BALANCE_ERROR_MESSAGE =
  'Numbers only. Disable loyalty balance to use text.';

/** Check whether the loyalty balance header field has a non-numeric default value. */
export const hasNonNumericLoyaltyBalance = (
  loyaltyBalanceEnabled: boolean,
  headerOneDefaultValue: unknown
): boolean => {
  const value = String(headerOneDefaultValue ?? '');
  return loyaltyBalanceEnabled && value !== '' && isNaN(Number(value));
};
